import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, Smartphone } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { Guarded } from "@/components/Guarded";
import { useLiveActionsDisabled } from "@/components/ConnectionBadge";
import { RunStatusPill } from "@/components/fleet/RunStatusPill";
import { StepTimeline } from "@/components/fleet/StepTimeline";
import { ExtractionTable } from "@/components/fleet/ExtractionTable";
import { TokenStat } from "@/components/fleet/TokenStat";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { useNow } from "@/components/ClockProvider";
import { useApiMutation, useApiQuery } from "@/hooks/useApi";
import { useTopic } from "@/hooks/useConsoleSocket";
import { qk, topicRun } from "@/lib/queryKeys";
import { post } from "@/lib/api";
import { S, T, runTriggerLabel } from "@/lib/strings";
import { absoluteTime, formatDuration, runElapsedMs } from "@/lib/utils";
import { fromRunStepFrame, fromStepRecord, mergeSteps, type TimelineStep } from "@/lib/steps";
import { isTerminal, type RunDetailResponse, type RunFrame, type StepRecord } from "@/types/fleet";

export function RunDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const now = useNow();
  const [stopOpen, setStopOpen] = useState(false);
  const [liveSteps, setLiveSteps] = useState<Map<number, TimelineStep>>(() => new Map());

  const run = useApiQuery<RunDetailResponse>(qk.run(id), `/runs/${id}`, {
    enabled: !!id,
  });

  const terminal = run.data ? isTerminal(run.data.status) : false;

  const steps = useApiQuery<{ items: StepRecord[] } | StepRecord[]>(
    qk.runSteps(id),
    `/runs/${id}/steps`,
    {
      enabled: !!id,
      // A finished run's steps never change; stop polling them.
      refetchInterval: terminal ? false : 60_000,
    },
  );

  // The steps endpoint is documented as "from DB; if absent and the device is
  // online, issues run.steps.fetch and backfills" without pinning the envelope,
  // so accept both a bare array and the {items} page shape.
  const persisted = useMemo<TimelineStep[]>(() => {
    const raw = steps.data;
    const list = Array.isArray(raw) ? raw : (raw?.items ?? []);
    return list.map(fromStepRecord);
  }, [steps.data]);

  useTopic(
    id && !terminal ? topicRun(id) : null,
    useCallback(
      (d: unknown) => {
        const frame = d as RunFrame;
        if (!frame || typeof frame !== "object") return;
        if (frame.kind === "run.step") {
          const step = fromRunStepFrame(frame);
          setLiveSteps((prev) => {
            const next = new Map(prev);
            next.set(step.n, step);
            return next;
          });
        } else if (frame.kind === "run.finished" || frame.kind === "run.started") {
          // The run's own row changed shape; let the query be the truth.
          void qc.invalidateQueries({ queryKey: qk.run(id) });
          void qc.invalidateQueries({ queryKey: qk.runSteps(id) });
        }
      },
      [id, qc],
    ),
  );

  const timeline = useMemo(() => mergeSteps(persisted, liveSteps), [persisted, liveSteps]);

  const stop = useApiMutation<void, void>(() => post<void>(`/runs/${id}/stop`), {
    invalidates: [qk.run(id)],
    successMessage: S.stopRunSent,
    errorTitle: S.mutationFailed,
    onSuccess: () => setStopOpen(false),
  });

  const live = useLiveActionsDisabled();

  if (run.isError) {
    return (
      <>
        <BackLink />
        <QueryErrorCard error={run.error} onRetry={() => void run.refetch()} />
      </>
    );
  }

  const r = run.data;

  return (
    <>
      <BackLink />

      <PageHeader
        title={r ? r.taskName : S.runDetailTitle}
        hint={
          r ? (
            <span className="font-mono">
              {r.id}
              {r.device && (
                <>
                  {" · "}
                  <Link
                    to={`/devices/${r.device.id}`}
                    className="hover:text-[var(--color-fg)] hover:underline"
                  >
                    {r.device.label}
                  </Link>
                </>
              )}
            </span>
          ) : undefined
        }
        actions={
          r && !isTerminal(r.status) ? (
            <Guarded permission="stop_run">
              <Button
                variant="danger"
                onClick={() => setStopOpen(true)}
                disabled={live.disabled}
                title={live.reason}
              >
                <Ban size={13} />
                {S.stopRun}
              </Button>
            </Guarded>
          ) : undefined
        }
      />

      {/* ---- summary ---- */}
      {run.isPending || !r ? (
        <Skeleton className="mb-5 h-[132px] w-full" />
      ) : (
        <Card className="mb-5">
          <CardContent className="pt-4">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <RunStatusPill status={r.status} error={r.error} />
              <span className="text-xs text-[var(--color-fg-subtle)]">
                {runTriggerLabel(r.trigger)}
              </span>
              {r.device && (
                <Link
                  to={`/devices/${r.device.id}`}
                  className="inline-flex items-center gap-1 text-xs text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]"
                >
                  <Smartphone size={11} />
                  {r.device.label}
                </Link>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-5">
              <Field label={S.steps} value={<span className="tabular-nums">{r.steps}</span>} />
              <Field
                label={S.duration}
                value={
                  <span className="tabular-nums">{formatDuration(runElapsedMs(r, now))}</span>
                }
              />
              <Field
                label={S.tokens}
                value={
                  <TokenStat
                    prompt={r.promptTokens}
                    completion={r.completionTokens}
                    reasoning={r.reasoningTokens}
                  />
                }
              />
              <Field
                label={S.extractions}
                value={<span className="tabular-nums">{r.extraction_count ?? r.extractions}</span>}
              />
              <Field label={S.colStarted} value={absoluteTime(r.startedAt ?? r.createdAt)} />
            </div>

            <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
                {S.goal}
              </div>
              <p className="mt-0.5 text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
                {r.goal}
              </p>
            </div>

            {r.summary && (
              <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
                <div className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
                  {S.summary}
                </div>
                <p className="mt-0.5 text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
                  {r.summary}
                </p>
              </div>
            )}

            {r.error && (
              <div className="mt-3 rounded-md border border-[rgba(239,68,68,0.2)] bg-[rgba(239,68,68,0.06)] p-2.5">
                <div className="text-[11px] font-medium uppercase tracking-wider text-[#fca5a5]">
                  {S.error}
                </div>
                <p className="mt-0.5 break-words font-mono text-[12px] text-[#fca5a5]">{r.error}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- extracted data ---- */}
      {!!r && (r.extraction_count ?? r.extractions) > 0 && (
        <div className="mb-5">
          <div className="mb-2 flex items-baseline gap-2">
            <h2 className="text-sm font-medium tracking-[-0.165px]">{S.extractions}</h2>
            <span className="text-[11px] text-[var(--color-fg-subtle)]">
              · {r.extraction_count ?? r.extractions}
            </span>
          </div>
          {/* The data is the point of the run, so it sits above the timeline: an operator opens
              this page to see what was collected far more often than to audit how. */}
          <ExtractionTable filter={{ run_id: id }} hideTaskColumn />
        </div>
      )}

      {/* ---- timeline ---- */}
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-sm font-medium tracking-[-0.165px]">{S.timeline}</h2>
        {r && !isTerminal(r.status) && (
          <span className="text-[11px] text-[#f59e0b]">· {S.liveTail}</span>
        )}
      </div>

      {steps.isError ? (
        <QueryErrorCard error={steps.error} onRetry={() => void steps.refetch()} />
      ) : (
        <StepTimeline
          steps={timeline}
          runId={id}
          live={!!r && !isTerminal(r.status)}
          loading={steps.isPending && timeline.length === 0}
        />
      )}

      <Dialog
        open={stopOpen}
        onOpenChange={setStopOpen}
        title={S.stopRun}
        description={S.stopRunConfirm}
        footer={
          <>
            <Button variant="ghost" onClick={() => setStopOpen(false)} disabled={stop.isPending}>
              {S.cancel}
            </Button>
            <Button variant="danger" onClick={() => stop.mutate()} disabled={stop.isPending}>
              {S.stop}
            </Button>
          </>
        }
      >
        {r && (
          <p className="text-xs text-[var(--color-fg-subtle)]">
            {T.runOf(r.taskName, r.device?.label ?? "—")}
          </p>
        )}
      </Dialog>
    </>
  );
}

function BackLink() {
  return (
    <Link
      to="/runs"
      className="mb-3 inline-flex items-center gap-1 text-[12px] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]"
    >
      <ArrowLeft size={12} />
      {S.runsTitle}
    </Link>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)]">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-[13px] text-[var(--color-fg)]">{value}</div>
    </div>
  );
}
